import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { FaPaperPlane, FaXmark } from 'react-icons/fa6';
import { useRotatingText } from '../hooks/useRotatingText';
import { textboxPlaceholders } from '../utils/phrases';

interface MessageCloudModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (message: string) => Promise<void>;
}

export const MessageCloudModal = ({ isOpen, onClose, onSend }: MessageCloudModalProps) => {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const rotatingPlaceholder = useRotatingText(textboxPlaceholders, 2000);

  const handleSend = async () => {
    if (message.trim() === '' || isSending) return;

    setIsSending(true);
    try {
      await onSend(message);
      setMessage('');
      onClose();
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSend();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-4 bottom-24 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-lg z-50"
          >
            <div className="bg-white backdrop-blur-xl rounded-3xl shadow-2xl border-2 border-primary/20 p-6 sm:p-8 h-full sm:h-auto flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <FaPaperPlane className="text-primary text-lg" />
                  </div>
                  <h2 className="text-2xl font-script text-primary">Enviar Mensaje</h2>
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors"
                >
                  <FaXmark className="text-gray-600" />
                </button>
              </div>

              {/* Message Input */}
              <div className="relative flex-1 mb-4">
                <AnimatePresence mode="wait">
                  {!message && (
                    <motion.p
                      key={rotatingPlaceholder}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      className="absolute left-0 top-0 p-4 text-gray-500 pointer-events-none z-10"
                    >
                      {rotatingPlaceholder}
                    </motion.p>
                  )}
                </AnimatePresence>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={handleKeyPress}
                  className="w-full h-40 sm:h-48 p-4 bg-gray-100 rounded-2xl border-2 border-primary/20 text-gray-800 placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:outline-none transition relative resize-none"
                  autoFocus
                ></textarea>
              </div>

              {/* Footer */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 py-3 px-6 bg-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-300 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSend}
                  disabled={isSending || message.trim() === ''}
                  className="flex-1 py-3 px-6 bg-secondary text-primary font-bold rounded-xl hover:bg-primary hover:text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane />
                      Enviar
                    </>
                  )}
                </button>
              </div>

              {/* Hint */}
              <p className="text-xs text-gray-500 text-center mt-4">
                Presiona Ctrl+Enter (o Cmd+Enter) para enviar
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
